import { useState, useEffect, useCallback } from 'react';

const DEFAULT_THRESHOLD = 64;
const DEFAULT_EXIT_DELTA = 24;

export interface ScrollThresholdOptions {
  threshold?: number;
  exitDelta?: number;
}

export interface ScrollThresholdResult {
  scrolled: boolean;
}

/**
 * Stable scroll threshold hook that uses a hysteresis window to avoid rapid
 * toggling when the user hovers around the cutoff value. All logic relies on
 * `window.scrollY`; no observers are required.
 */
export function useScrollThreshold(options: ScrollThresholdOptions = {}): ScrollThresholdResult {
  const {
    threshold = DEFAULT_THRESHOLD,
    exitDelta = DEFAULT_EXIT_DELTA,
  } = options;

  const [scrolled, setScrolled] = useState(false);

  const updateState = useCallback(() => {
    if (typeof window === 'undefined') return;

    const currentY = window.scrollY || 0;
    const enterThreshold = threshold;
    const exitThreshold = Math.max(0, threshold - exitDelta);

    setScrolled((prev) => {
      if (!prev && currentY >= enterThreshold) {
        return true;
      }
      if (prev && currentY <= exitThreshold) {
        return false;
      }
      return prev;
    });
  }, [threshold, exitDelta]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    updateState();

    const handleScroll = () => {
      updateState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updateState]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      updateState();
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateState]);

  return { scrolled };
}
